import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimeAgo } from '../../i18n/format';

export const ActivityLog = () => {
    const { t } = useTranslation();
    const [activityLog, setActivityLog] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadActivityLog = async () => {
        try {
            const response = await fetch('api/activity');
            const data = await response.json();
            setActivityLog(data);
        } catch (error) {
            console.error('Failed to load activity log:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadActivityLog();
    }, []);

    function getBadgeClass(actionType) {
        const badgeMap = {
            'SurveyUploaded': 'bg-primary',
            'SurveyApproved': 'bg-success',
            'SurveyReturnedToApproval': 'bg-warning',
            'SurveyDeleted': 'bg-danger',
            'ProjectCreated': 'bg-info',
            'ProjectDeleted': 'bg-danger',
            'ProjectCompleted': 'bg-success',
            'ProjectReopened': 'bg-warning',
            'DrillholeCreated': 'bg-info',
            'DrillholeDeleted': 'bg-danger',
            'DrillholeCompleted': 'bg-success',
            'DrillholeReopened': 'bg-warning',
            'DrillholesImported': 'bg-primary'
        };
        return badgeMap[actionType] || 'bg-secondary';
    }

    // The action type is a stable code, so it is the translation key. A type the bundles do
    // not know yet (a new server action) falls back to the code split into words, which is
    // exactly what this label always showed.
    function formatActionType(actionType) {
        return t(`activity.actions.${actionType}`, {
            defaultValue: actionType.replace(/([A-Z])/g, ' $1').trim()
        });
    }

    function getContextString(entry) {
        const parts = [];
        if (entry.projectName) parts.push(entry.projectName);
        if (entry.drillholeName) parts.push(entry.drillholeName);
        if (entry.surveyFileName && !entry.summary.includes(entry.surveyFileName)) {
            parts.push(entry.surveyFileName);
        }
        return parts.length > 0 ? parts.join(' / ') : null;
    }

    if (loading) {
        return (
            <div className="container" style={{ marginTop: 10 }}>
                <h3>{t('activity.title')}</h3>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="container" style={{ marginTop: 10 }}>
            <h3>{t('activity.title')}</h3>
            {activityLog && activityLog.entries && activityLog.entries.length > 0 ? (
                <ul className="list-group">
                    {activityLog.entries.map((entry) => (
                        <li key={entry.id} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <span className={`badge ${getBadgeClass(entry.actionType)} me-2`}>
                                        {formatActionType(entry.actionType)}
                                    </span>
                                    {/* Written by the server as a finished English sentence, and
                                        stored that way for every past entry, so it stays English. */}
                                    <strong>{entry.summary}</strong>
                                    {getContextString(entry) && (
                                        <div className="text-muted small mt-1">
                                            {getContextString(entry)}
                                        </div>
                                    )}
                                </div>
                                <div className="text-end">
                                    <div className="text-muted small">{formatTimeAgo(entry.timestamp)}</div>
                                    <div className="text-muted small">
                                        {entry.actor}
                                        {entry.actorType === 'Device' && (
                                            <span className="badge bg-secondary ms-1">{t('activity.device')}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-muted">{t('activity.empty')}</p>
            )}
        </div>
    );
};
